<template>
  <div
      class="min-h-full flex flex-col bg-gradient-to-br from-background via-background/80 to-background/60 transition-all duration-300">
    <!-- Main Content -->
    <main class="container mx-auto px-4 flex-1 my-12 space-y-12">
      <!-- Welcome Message -->
      <div class="text-center">
        <h1 class="text-3xl leading-10 md:leading-16 md:text-4xl font-bold bg-gradient-to-r from-primary via-primary/90 to-primary/70 bg-clip-text text-transparent animate-fade-in">
          Chào mừng trở lại, {{ user.Name }}!
        </h1>
      </div>

      <!-- Banner Carousel -->
      <div class="max-w-5xl mx-auto">
        <Carousel class="w-full" v-slot="{ carouselApi }" :plugins="[autoplayPlugin]">
          <CarouselContent>
            <CarouselItem v-for="banner in banners" :key="banner.id">
              <div
                  class="relative overflow-hidden rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg"
                  @click="openBannerLink(banner.targetUrl)"
              >
                <img
                    :src="banner.imageUrl"
                    :alt="banner.title"
                    class="w-full h-auto object-cover"
                />
                <!--                <div-->
                <!--                    class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white">-->
                <!--                  <div class="flex items-center justify-between">-->
                <!--                    <h3 class="font-medium">{{ banner.title }}</h3>-->
                <!--                    <ExternalLink class="h-4 w-4 opacity-70"/>-->
                <!--                  </div>-->
                <!--                </div>-->
              </div>
            </CarouselItem>
          </CarouselContent>
          <!--          <CarouselPrevious class="hidden md:flex"/>-->
          <!--          <CarouselNext class="hidden md:flex"/>-->

          <!-- Carousel Indicators -->
          <div class="flex justify-center gap-1 mt-4">
            <button
                v-for="(_, index) in banners"
                :key="index"
                class="size-2 rounded-full transition-all"
                :class="[
                carouselApi?.selectedScrollSnap() === index
                  ? 'bg-primary'
                  : 'bg-primary/30'
              ]"
                @click="carouselApi?.scrollTo(index)"
                aria-label="Go to slide"
            />
          </div>
        </Carousel>
      </div>

      <div class="max-w-5xl mx-auto flex items-center justify-between">
        <h2 class="font-bold text-3xl bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
          Minigame</h2>
        <Button
            @click="openAddTurnModal" v-if="spinMinigame || puzzleMinigame"
            class="h-10 px-5 text-md font-medium bg-primary/90 text-white hover:bg-primary shadow-md hover:shadow-lg transition-all duration-300 rounded-full hover:cursor-pointer">
          Thêm lượt
        </Button>

        <!-- Add Turn Modal -->
        <AddTurnModal v-if="showAddTurnModal" @close="closeAddTurnModal"/>
      </div>

      <!-- Game Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" v-if="spinMinigame || puzzleMinigame">
        <!-- Card 1: Vòng Quay May Mắn -->
        <div v-if="spinMinigame"
             class="group relative overflow-hidden rounded-3xl transition-all duration-500 shadow-lg shadow-primary/10 dark:shadow-[0_8px_32px_rgba(80,120,255,0.3),0_2px_12px_rgba(0,0,0,0.4)] hover:shadow-2xl hover:shadow-primary/20 dark:hover:shadow-[0_12px_48px_rgba(80,120,255,0.4),0_4px_24px_rgba(0,0,0,0.5)] active:scale-[0.98] active:shadow-inner cursor-pointer"
             @click="navigateToGame('/vong-quay-may-man')"
        >
          <!-- Card Border Gradient Animation -->
          <div class="absolute inset-0 z-10 pointer-events-none rounded-3xl">
            <div class="w-full h-full rounded-3xl animate-border-glow"></div>
          </div>
          <!-- Card Content -->
          <div
              class="relative h-full bg-gradient-to-br from-card via-card to-card/90 rounded-3xl p-8 flex flex-col transition-all duration-300 backdrop-blur-[2px] group-hover:backdrop-blur-none border border-primary/30 dark:border-primary/40 group-hover:border-primary/50 dark:group-hover:border-primary/70 z-20">
            <div class="flex justify-between items-start mb-4">
              <h3 class="text-xl font-bold bg-gradient-to-r from-primary/90 to-primary/70 bg-clip-text text-transparent">
                {{ spinMinigame.Name }}</h3>
              <div
                  class="bg-gradient-to-r from-primary/30 to-primary/20 text-primary px-3 py-1 rounded-full text-xs font-medium border border-primary/20 shadow-sm">
                {{ spinMinigame.NumRemaining }} lượt
              </div>
            </div>

            <div class="flex-1 flex items-center justify-center py-4">
              <div
                  class="w-32 h-32 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center group-hover:scale-110 transition-all duration-500 shadow-md shadow-primary/10 group-hover:shadow-lg group-hover:shadow-primary/20">
                <div
                    class="w-24 h-24 rounded-full bg-gradient-to-br from-primary via-primary/80 to-primary/60 flex items-center justify-center text-white animate-pulse-slow shadow-inner">
                  <div class="transform rotate-0 group-hover:rotate-[360deg] transition-transform duration-[3000ms]">
                    <CircleArrowRight class="h-10 w-10"/>
                  </div>
                </div>
              </div>
            </div>

            <p class="text-sm text-muted-foreground mt-2 group-hover:text-primary/70 transition-colors duration-300">
              {{ spinMinigame.Description }}</p>
            <p class="text-sm text-muted-foreground mt-2 group-hover:text-primary/70 transition-colors duration-300">
              Đơn áp dụng: {{ customizeDateTime(spinMinigame.StartDate, 'DD/MM/YYYY') }} -
              {{ customizeDateTime(spinMinigame.EndDate, 'DD/MM/YYYY') }}</p>
          </div>
        </div>

        <!-- Card 2: Ghép Ô Bí Ẩn -->
        <div v-if="puzzleMinigame"
             class="group relative overflow-hidden rounded-3xl transition-all duration-500 shadow-lg shadow-primary/10 dark:shadow-[0_8px_32px_rgba(80,120,255,0.3),0_2px_12px_rgba(0,0,0,0.4)] hover:shadow-2xl hover:shadow-primary/20 dark:hover:shadow-[0_12px_48px_rgba(80,120,255,0.4),0_4px_24px_rgba(0,0,0,0.5)] active:scale-[0.98] active:shadow-inner cursor-pointer"
             @click="navigateToGame('/ghep-o-bi-an')"
        >
          <!-- Card Border Gradient Animation -->
          <div class="absolute inset-0 z-10 pointer-events-none rounded-3xl">
            <div class="w-full h-full rounded-3xl animate-border-glow"></div>
          </div>
          <!-- Card Content -->
          <div
              class="relative h-full bg-gradient-to-br from-card via-card to-card/90 rounded-3xl p-8 flex flex-col transition-all duration-300 backdrop-blur-[2px] group-hover:backdrop-blur-none border border-primary/30 dark:border-primary/40 group-hover:border-primary/50 dark:group-hover:border-primary/70 z-20">
            <div class="flex justify-between items-start mb-4">
              <h3 class="text-xl font-bold bg-gradient-to-r from-primary/90 to-primary/70 bg-clip-text text-transparent">
                {{ puzzleMinigame.Name }}</h3>
              <div
                  class="bg-gradient-to-r from-primary/30 to-primary/20 text-primary px-3 py-1 rounded-full text-xs font-medium border border-primary/20 shadow-sm">
                {{ puzzleMinigame.NumRemaining }} lượt
              </div>
            </div>

            <div class="flex-1 flex items-center justify-center py-4">
              <div
                  class="grid grid-cols-2 gap-2 group-hover:scale-110 transition-all duration-500 shadow-md shadow-primary/10 group-hover:shadow-lg group-hover:shadow-primary/20 p-4 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 animate-pulse-slow">
                <div
                    class="w-12 h-12 bg-gradient-to-br from-primary/40 to-primary/20 rounded-md transform transition-transform group-hover:rotate-12 duration-700 shadow-sm"></div>
                <div
                    class="w-12 h-12 bg-gradient-to-br from-primary/60 to-primary/40 rounded-md transform transition-transform group-hover:rotate-[-12deg] duration-700 shadow-sm"></div>
                <div
                    class="w-12 h-12 bg-gradient-to-br from-primary/80 to-primary/60 rounded-md transform transition-transform group-hover:rotate-[-12deg] duration-700 shadow-sm"></div>
                <div
                    class="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-md flex items-center justify-center text-white transform transition-transform group-hover:rotate-12 duration-700 shadow-sm">
                  <Puzzle class="h-6 w-6"/>
                </div>
              </div>
            </div>

            <p class="text-sm text-muted-foreground mt-2 group-hover:text-primary/70 transition-colors duration-300">
              {{ puzzleMinigame.Description }}</p>
            <p class="text-sm text-muted-foreground mt-2 group-hover:text-primary/70 transition-colors duration-300">
              Đơn áp dụng: {{ customizeDateTime(puzzleMinigame.StartDate, 'DD/MM/YYYY') }} -
              {{ customizeDateTime(puzzleMinigame.EndDate, 'DD/MM/YYYY') }}</p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 max-w-5xl mx-auto" v-else>
        <p class="text-lg">Hiện tại, không có Minigame nào diễn ra đang diễn ra. Vui lòng cập nhật thông tin mới nhất tại Fanpage: <a class="font-bold text-blue-500" href="https://www.facebook.com/drnguyenduycuong" target="_blank">DR.CƯƠNG</a></p>
      </div>
    </main>

<!--    <popup v-show="isShowPopup" :is-open="isShowPopup" v-on:close-modal="closePopup"></popup>-->
  </div>
</template>

<script setup lang="ts">
import {ref, onMounted, computed, nextTick, defineAsyncComponent} from 'vue';
import {useAuthStore} from '@/stores/auth';
import {useRouter} from 'vue-router';
import {useLoading} from '@/composables/useLoading';
import {useImageLoading} from '@/composables/useImageLoading';
import {CircleArrowRight, Puzzle} from "lucide-vue-next";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import {Button} from "@/components/ui/button";
import AddTurnModal from '@/components/AddTurnModal.vue';
import {i18nMinigame} from "@/i18n/i18nCommon.tsx";
import {customizeDateTime} from "@/js/functions/convertDateTime.ts";

const popup = defineAsyncComponent(() => import('../components/Popup.vue'))

const auth = useAuthStore();
const router = useRouter();

const user = computed(() => auth.user || {Name: '', Email: '', Phone: ''});
const spinMinigame: any = computed(() => auth.minigame?.find((x: any) => x.Code == i18nMinigame.Spin.Code));
const puzzleMinigame: any = computed(() => auth.minigame?.find((x: any) => x.Code == i18nMinigame.Puzzle.Code));

// Add Turn Modal state
const showAddTurnModal = ref(false);
const isShowPopup = ref(false);

// Banner data for carousel
const banners = ref([
  {
    id: 1,
    imageUrl: '/img/BANNER_1.png',
    targetUrl: 'https://www.facebook.com/drnguyenduycuong/posts/pfbid02Fubxj4T6JhHnLGzgszN8mcKthbNSaazCqEL9yNygfhL5zXSSkTwkUSjVJGjMw1qWl',
    title: 'Khuyến mãi đặc biệt'
  }
]);

// Create autoplay plugin instance
const autoplayPlugin = ref(
    Autoplay({
      delay: 3500, // 5 seconds
      stopOnInteraction: false, // Continue autoplay after user interaction
      stopOnMouseEnter: true, // Pause on mouse hover
    })
);

onMounted(() => {
  // Wait for all images to be loaded before hiding the loading overlay
  const {waitForImages} = useImageLoading();
  const {stopLoading} = useLoading();

  if (!localStorage.getItem('isHidePopup')) {
    localStorage.setItem('isHidePopup', 'false');
  }

  isShowPopup.value = localStorage.getItem('isHidePopup') == 'false';

  // Use nextTick to ensure the DOM is updated with the new data
  nextTick(async () => {
    // Wait for all images to be loaded
    await waitForImages();
    // Then hide the loading overlay
    stopLoading();
  })
})


function navigateToGame(path: string) {
  router.push(path);
}

// Function to handle banner clicks
function openBannerLink(url: string) {
  // Open the URL in a new tab
  window.open(url, '_blank');
}

// Functions for Add Turn Modal
function openAddTurnModal() {
  showAddTurnModal.value = true;
}

function closeAddTurnModal() {
  showAddTurnModal.value = false;
}

const closePopup = () => {
  isShowPopup.value = false
}
</script>

<style>
@keyframes border-glow {
  0% {
    background-position: 0 50%;
    filter: hue-rotate(0deg) brightness(1);
  }
  50% {
    filter: hue-rotate(180deg) brightness(1.2);
  }
  100% {
    background-position: 200% 50%;
    filter: hue-rotate(360deg) brightness(1);
  }
}

.animate-border-glow {
  background: linear-gradient(120deg, #60A5FA, #3B82F6, #2563EB, #1D4ED8, #60A5FA);
  background-size: 200% 200%;
  animation: border-glow 6s linear infinite;
  /* Tạo border bằng mask */
  -webkit-mask-image: linear-gradient(#fff 0 0), linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
  -webkit-mask-clip: content-box, padding-box, border-box;
  -webkit-mask-composite: xor, xor;
  padding: 4px;
  /* Đảm bảo chỉ hiện border ngoài */
  mask-image: linear-gradient(#fff 0 0), linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
  mask-clip: content-box, padding-box, border-box;
  mask-composite: exclude, exclude;
  border-radius: 1.5rem; /* tương ứng rounded-3xl */
  pointer-events: none;
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.3);
}

@keyframes border-flow-chase {
  0% {
    background-position: 0 50%;
  }
  25% {
    background-position: 100% 0;
  }
  50% {
    background-position: 100% 50%;
  }
  75% {
    background-position: 0 100%;
  }
  100% {
    background-position: 0 50%;
  }
}

@keyframes border-flow-rainbow {
  0% {
    background-position: 0 50%;
    background-image: linear-gradient(to right, var(--primary), color-mix(in srgb, var(--primary) 70%, transparent), var(--primary));
    filter: brightness(1) saturate(1);
  }
  25% {
    background-position: 100% 0;
    background-image: linear-gradient(to right, color-mix(in srgb, var(--primary) 70%, transparent), var(--primary), color-mix(in srgb, var(--primary) 70%, transparent));
    filter: brightness(1.1) saturate(1.2);
  }
  50% {
    background-position: 100% 50%;
    background-image: linear-gradient(to right, var(--primary), color-mix(in srgb, var(--primary) 70%, transparent), var(--primary));
    filter: brightness(1.2) saturate(1.3);
  }
  75% {
    background-position: 0 100%;
    background-image: linear-gradient(to right, color-mix(in srgb, var(--primary) 70%, transparent), var(--primary), color-mix(in srgb, var(--primary) 70%, transparent));
    filter: brightness(1.1) saturate(1.2);
  }
  100% {
    background-position: 0 50%;
    background-image: linear-gradient(to right, var(--primary), color-mix(in srgb, var(--primary) 70%, transparent), var(--primary));
    filter: brightness(1) saturate(1);
  }
}

.animate-fade-in {
  animation: fade-in 1s ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1) saturate(1);
    box-shadow: 0 0 5px color-mix(in srgb, var(--primary) 30%, transparent);
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
    filter: brightness(1.1) saturate(1.2);
    box-shadow: 0 0 15px color-mix(in srgb, var(--primary) 50%, transparent);
  }
  100% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1) saturate(1);
    box-shadow: 0 0 5px color-mix(in srgb, var(--primary) 30%, transparent);
  }
}

.animate-pulse-slow {
  animation: pulse 4s ease-in-out infinite;
}

/* Hiệu ứng border gradient động chạy quanh card */
@keyframes border-gradient {
  0% {
    border-image: linear-gradient(90deg, #60A5FA, #3B82F6, #2563EB, #1D4ED8, #60A5FA) 1;
    filter: hue-rotate(0deg) brightness(1);
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.3);
  }
  50% {
    filter: hue-rotate(180deg) brightness(1.2);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
  }
  100% {
    border-image: linear-gradient(450deg, #60A5FA, #3B82F6, #2563EB, #1D4ED8, #60A5FA) 1;
    filter: hue-rotate(360deg) brightness(1);
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.3);
  }
}
</style>
